import { isNil } from 'es-toolkit';
import { DateTime } from 'luxon';

import type {
  TaxEnrollmentStatus,
  TaxEnrollmentTypeName,
  TaxReturnStatus,
  TaxTranscriptRequestStatus,
} from '@savvy/constants/generated/gql-enums';

// Types
export type TaxFlowType = 'transcript' | 'filing' | 'not_interested' | 'not_enrolled';

export type TaxTranscriptRequest = {
  readonly id: string;
  readonly status: TaxTranscriptRequestStatus;
  readonly updatedAt: string;
};

export type TaxEnrollment = {
  readonly id: string;
  readonly enrollmentType: TaxEnrollmentTypeName;
  readonly taxYear: number;
  readonly price?: string | null;
  readonly federalReturnStatus?: TaxReturnStatus | null;
  readonly stateReturnStatus?: TaxReturnStatus | null;
  readonly updatedAt?: string | null;
};

export type SelectedClient = {
  readonly id: string;
  readonly clientId: string | null;
  readonly taxEnrollmentId: string | null;
  readonly salesforceId: string | null;
};

export type ClientForAddendumCheck = {
  readonly onboardingIaaCreatedAt?: string | null;
  readonly clientAgreements?:
    | readonly {
        readonly agreementType: string;
        readonly signatureId?: string | null;
      }[]
    | null;
};

// Constants
export const CURRENT_TAX_YEAR = 2025;

//TODO: update cutoff date once we have updated IAAs for new clients to sign
export const TAX_IAA_CUTOFF_DATE = DateTime.fromISO('2027-02-28');

export const TRANSCRIPT_STEPS = ['Waiting for signature', 'Waiting for the IRS', 'Tax data available'] as const;

export const FILING_STEPS = [
  'Waiting for signature',
  'Filing in progress',
  'Partial filing submitted',
  'State and Federal submitted',
  'Partial filing approved',
  'Federal and state accepted. Tax filing complete.',
] as const;

/** Filing steps when client must sign tax addendum between transcript consent and filing */
export const FILING_STEPS_WITH_ADDENDUM = [
  'Waiting for signature',
  'Sign tax addendum',
  'Filing in progress',
  'Partial filing submitted',
  'State and Federal submitted',
  'Partial filing approved',
  'Federal and state accepted. Tax filing complete.',
] as const;

export const DEFAULT_CONSENT_TEXT = `Allow april to retrieve your tax transcript and share with Savvy\n\napril gathers information from the IRS on behalf of Savvy to make it easier for you to apply for services from Savvy.\n\nBy typing your name within the signature block below, you are completing and signing IRS Form 8821, which authorizes the IRS to disclose to april your tax transcript (including your Forms 1040, W-2, 1099) from the past 3 years, current year, and up to 2 future years.\nAccess to your tax transcript is view-only and does not permit april or Savvy to take any action that would change your tax return.\n\nNote: Typing your name into the signature block and clicking 'Submit' will constitute your legally binding signature. If you have previously used Form 8821 to allow someone else to access your tax transcript, signing this form will override your prior authorization.\n\nBy clicking 'Submit', you authorize april to submit Form 8821 to the IRS and to receive your tax transcript on behalf of Savvy\n`;

// Functions
/** Returns true if the client's IAA predates the cutoff, meaning a tax addendum is part of their flow. */
export function isTaxAddendumRequired(showTaxAddendum: boolean, client?: ClientForAddendumCheck): boolean {
  if (!showTaxAddendum) {
    return false;
  } else if (isNil(client?.onboardingIaaCreatedAt)) {
    return true;
  }
  return DateTime.fromISO(client.onboardingIaaCreatedAt) < TAX_IAA_CUTOFF_DATE;
}

/** Returns true if the client still needs to sign the tax addendum (required + not yet signed). */
export function isTaxAddendumPending(showTaxAddendum: boolean, client?: ClientForAddendumCheck): boolean {
  if (!isTaxAddendumRequired(showTaxAddendum, client)) {
    return false;
  }
  return (
    client?.clientAgreements?.some(
      (agreement) => agreement.agreementType === 'tax_addendum' && !isNil(agreement.signatureId),
    ) !== true
  );
}

// Formatting
export const formatTaxEnrollment = (taxEnrollment: TaxEnrollmentTypeName) => {
  switch (taxEnrollment) {
    case 'savvy_single':
      return 'Single filing';
    case 'savvy_joint':
      return 'Joint filing';
    case 'external_cpa':
      return 'External CPA';
    case 'transcript_services_only':
      return 'Transcript services only';
    case 'other':
      return 'Other';
    default:
      return 'No active tax service';
  }
};

// Casting
export const castToTaxEnrollmentType = (value: string): TaxEnrollmentTypeName => {
  const validValues: TaxEnrollmentTypeName[] = [
    'savvy_single',
    'savvy_joint',
    'external_cpa',
    'other',
    'transcript_services_only',
  ];
  return validValues.find((v) => v === value) ?? 'savvy_single';
};

export const castToTaxEnrollmentStatus = (value: string): TaxEnrollmentStatus => {
  const validValues: TaxEnrollmentStatus[] = [
    'not_interested',
    'not_started',
    'registered',
    'in_progress',
    'federal_submitted',
    'federal_accepted',
    'federal_rejected',
    'state_submitted',
    'state_accepted',
    'state_rejected',
  ];
  return validValues.find((v) => v === value) ?? 'not_started';
};

export const castToTaxTranscriptStatus = (status: string) => {
  const validValues: TaxTranscriptRequestStatus[] = ['requested', 'completed'];
  return validValues.find((v) => v === status) ?? null;
};

// Transcript utilities
export const getTranscriptStep = (status: TaxTranscriptRequestStatus | null): number => {
  switch (status) {
    case null:
      return 0;
    case 'requested':
      return 1;
    case 'completed':
      return 2;
    default:
      return 0;
  }
};

export function getLastTranscriptStatus(
  requests: readonly TaxTranscriptRequest[] | null | undefined,
): TaxTranscriptRequestStatus | null {
  if (!requests) return null;
  return requests[0]?.status ?? null;
}

// Filing utilities
export const getFilingStep = (
  federalStatus: TaxReturnStatus | null,
  stateStatus: TaxReturnStatus | null,
  taxTranscriptRequested: boolean,
  taxAddendumPending: boolean,
  taxAddendumRequired: boolean,
): number => {
  const addendumStepAdjustment = taxAddendumRequired ? 1 : 0;

  if (!taxTranscriptRequested) {
    return 0;
  }
  if (taxAddendumPending) {
    return 1;
  }
  if (federalStatus === 'accepted' && stateStatus === 'accepted') {
    return 5 + addendumStepAdjustment;
  }
  if (federalStatus === 'accepted' || stateStatus === 'accepted') {
    return 4 + addendumStepAdjustment;
  }
  if (federalStatus === 'filed_pending_response' && stateStatus === 'filed_pending_response') {
    return 3 + addendumStepAdjustment;
  }
  if (federalStatus === 'filed_pending_response' || stateStatus === 'filed_pending_response') {
    return 2 + addendumStepAdjustment;
  }
  if (
    (federalStatus === 'none' && stateStatus === 'none') ||
    (federalStatus === 'not_filed' && stateStatus === 'not_filed')
  ) {
    return 1 + addendumStepAdjustment;
  }
  // default to filing in progress
  return 1 + addendumStepAdjustment;
};

// Flow and enrollment utilities
export const getCurrentTaxFlow = (tax_enrollment: TaxEnrollmentTypeName | null, tax_filing_enabled: boolean) => {
  let clientFlow: TaxFlowType = 'not_enrolled';

  if (!tax_enrollment) {
    return clientFlow;
  }
  if (tax_enrollment === 'external_cpa' || tax_enrollment === 'other') {
    return 'not_interested';
  }

  clientFlow = 'transcript';

  if (tax_filing_enabled && ['savvy_single', 'savvy_joint'].includes(tax_enrollment)) {
    clientFlow = 'filing';
  }
  return clientFlow;
};

export function getCurrentYearEnrollment(taxEnrollments: TaxEnrollment[] | null | undefined) {
  if (!taxEnrollments) return null;
  return taxEnrollments.find((enrollment) => enrollment.taxYear === CURRENT_TAX_YEAR)?.enrollmentType ?? null;
}

export const isClientFilingTaxes = (enrollmentType: TaxEnrollmentTypeName) => {
  switch (enrollmentType) {
    case 'external_cpa':
    case 'other':
    case 'transcript_services_only':
      return false;
    case 'savvy_joint':
    case 'savvy_single':
      return true;
  }
};

export const isAtLeastOneClientFiling = (
  primaryEnrollmentType: TaxEnrollmentTypeName,
  hasCoClient: boolean,
  coClientEnrollmentType: TaxEnrollmentTypeName,
) => {
  if (!hasCoClient) {
    return isClientFilingTaxes(primaryEnrollmentType);
  }
  return isClientFilingTaxes(primaryEnrollmentType) || isClientFilingTaxes(coClientEnrollmentType);
};

export const getNotificationTimingText = (enableTaxFiling: boolean, isLateFiling: boolean) => {
  const immediately = `This will send emails to the client to continue ${CURRENT_TAX_YEAR} tax filing.`;
  const laterThisSeason = 'Emails will be sent to continue filing after the deadline.';
  const nextTaxSeason = `We will send emails to the client around January ${CURRENT_TAX_YEAR + 2} for the ${CURRENT_TAX_YEAR + 1} filing season.`;

  if (enableTaxFiling) {
    return isLateFiling ? laterThisSeason : immediately;
  }
  return isLateFiling ? immediately : nextTaxSeason;
};
